import type { ExerciseSummary, RhythmScore } from '@/lib/exercise/rhythmEngine';
import { assessRhythmAttempt, RHYTHM_ITEM_LABEL, type RhythmItemKey } from '@/lib/exercise/rhythmSummary';
import { RHYTHM_ACCURACY_GOAL } from '@/lib/progress/gating';

interface RhythmSummaryPanelProps {
  score: RhythmScore;
  history: ExerciseSummary[];
  hasRests: boolean;
  hasTies: boolean;
  hasDuration: boolean;
  accuracy: number;
}

function StatusPill({ correct, wrong }: { correct: number; wrong: number }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="inline-flex items-center gap-1 font-heading text-sm font-bold text-accent-700">
        <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
        {correct}
      </span>
      <span className="inline-flex items-center gap-1 font-heading text-sm font-bold text-secondary-700">
        <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center" />
        {wrong}
      </span>
    </span>
  );
}

/** Ícone/cor do estado de cada item exigido: passou, falhou ou ficou sem medir. */
function RequiredItemRow({
  keyName,
  correct,
  wrong,
  notEvaluated,
}: {
  keyName: RhythmItemKey;
  correct: number;
  wrong: number;
  notEvaluated: boolean;
}) {
  const total = correct + wrong;
  const itemAccuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
  const passed = itemAccuracy >= RHYTHM_ACCURACY_GOAL && !notEvaluated;
  return (
    <li
      className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ${
        notEvaluated ? 'bg-background-100/70' : passed ? 'bg-accent-50' : 'bg-secondary-50'
      }`}
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-label font-semibold text-foreground-800">
        <i
          className={`${
            notEvaluated
              ? 'ri-question-line text-foreground-500'
              : passed
                ? 'ri-check-line text-accent-600'
                : 'ri-close-line text-secondary-700'
          } w-3.5 h-3.5 flex items-center justify-center`}
        />
        {RHYTHM_ITEM_LABEL[keyName]}
      </span>
      <span className="shrink-0 text-[11px] font-label font-semibold">
        {notEvaluated ? (
          <span className="text-foreground-500">não avaliada</span>
        ) : passed ? (
          <span className="text-accent-700">{itemAccuracy}% · dominado</span>
        ) : (
          <span className="text-secondary-700">{itemAccuracy}% · abaixo da meta</span>
        )}
      </span>
    </li>
  );
}

/**
 * Resumo final da sessão de ritmo, item por item. Mostra a REGRA exata de
 * desbloqueio: quais itens a aula exige e qual erro impediu a aula de ser
 * dominada. Também lista, em cada exercício, o que foi contado como certo e
 * como errado em cada dimensão (nota, ataque, duração, pausa e ligadura).
 */
export default function RhythmSummaryPanel({
  score,
  history,
  hasRests,
  hasTies,
  hasDuration,
  accuracy,
}: RhythmSummaryPanelProps) {
  const context = { hasRests, hasTies, measuresDuration: hasDuration };
  const assessment = assessRhythmAttempt(score, context);

  const statePill = assessment.mastered
    ? { label: `Aula dominada · meta ${RHYTHM_ACCURACY_GOAL}%`, cls: 'bg-accent-100 text-accent-800', icon: 'ri-medal-line' }
    : assessment.errorFree
      ? { label: 'Sem erros · falta medir algum item', cls: 'bg-primary-100 text-primary-800', icon: 'ri-check-double-line' }
      : { label: 'Continue praticando até a meta', cls: 'bg-secondary-100 text-secondary-800', icon: 'ri-refresh-line' };

  return (
    <div className="mt-5 w-full rounded-lg border border-background-200 bg-background-50 p-4 text-left md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <i className="ri-file-list-3-line w-5 h-5 flex items-center justify-center text-primary-600" />
          <span className="font-heading text-base font-bold text-foreground-950">
            Resumo final por item
          </span>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-label font-semibold ${statePill.cls}`}
        >
          <i className={`${statePill.icon} w-3.5 h-3.5 flex items-center justify-center`} />
          {accuracy}% de precisão · {statePill.label}
        </span>
      </div>

      {/* Regra exata de desbloqueio */}
      <div className="mt-4 rounded-lg border border-background-200 bg-background-100/60 p-3">
        <span className="inline-flex items-center gap-1.5 font-label text-xs font-bold text-foreground-800">
          <i className="ri-key-2-line w-4 h-4 flex items-center justify-center text-primary-600" />
          O que esta aula exigiu para desbloquear
        </span>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {assessment.items.map((item) => (
            <RequiredItemRow
              key={item.key}
              keyName={item.key}
              correct={item.correct}
              wrong={item.wrong}
              notEvaluated={item.notEvaluated}
            />
          ))}
        </ul>
        <p className="mt-2 text-[11px] leading-relaxed text-foreground-600">
          {assessment.mastered
            ? `Todos os itens exigidos atingiram pelo menos ${RHYTHM_ACCURACY_GOAL}%: a próxima aula está desbloqueada.`
            : assessment.errorFree
              ? 'Os itens medidos ficaram perfeitos, mas algum item não pôde ser medido neste aparelho — por isso a aula ainda não conta como dominada.'
              : `Ainda falta atingir ${RHYTHM_ACCURACY_GOAL}% em: ${assessment.failedKeys
                  .map((key) => RHYTHM_ITEM_LABEL[key])
                  .join(', ')}.`}
        </p>
      </div>

      {/* Total da sessão, item por item */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {assessment.items.map((item) => (
          <div key={item.key} className="rounded-lg border border-background-200 bg-background-100/60 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 font-label text-xs font-bold text-foreground-800">
                <i className="ri-check-double-line w-4 h-4 flex items-center justify-center text-primary-600" />
                {item.label}
              </span>
              {item.notEvaluated ? (
                <span className="text-[11px] font-label font-semibold text-foreground-500">
                  não avaliada
                </span>
              ) : (
                <StatusPill correct={item.correct} wrong={item.wrong} />
              )}
            </div>
          </div>
        ))}
      </div>

      {history.length > 0 ? (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <i className="ri-list-check-2 w-4 h-4 flex items-center justify-center text-primary-600" />
            <span className="font-label text-sm font-bold text-foreground-800">
              O que foi contado em cada exercício
            </span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-background-200">
            <table className="w-full min-w-[520px] border-collapse text-left">
              <thead>
                <tr className="bg-background-100 text-[11px] font-label font-bold text-foreground-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Compasso</th>
                  <th className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-music-2-line w-4 h-4 flex items-center justify-center" /> Nota
                    </span>
                  </th>
                  <th className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-timer-line w-4 h-4 flex items-center justify-center" /> Ataque
                    </span>
                  </th>
                  <th className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-hourglass-line w-4 h-4 flex items-center justify-center" /> Duração
                    </span>
                  </th>
                  {hasRests ? (
                    <th className="px-3 py-2">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-pause-circle-line w-4 h-4 flex items-center justify-center" /> Pausa
                      </span>
                    </th>
                  ) : null}
                  {hasTies ? (
                    <th className="px-3 py-2">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-link w-4 h-4 flex items-center justify-center" /> Ligadura
                      </span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => {
                  const perItem = assessRhythmAttempt(entry.score, context).items;
                  const byKey = Object.fromEntries(perItem.map((item) => [item.key, item]));
                  return (
                    <tr key={entry.index} className="border-t border-background-200 text-sm">
                      <td className="px-3 py-2 font-heading font-bold text-foreground-950">
                        {entry.index + 1}
                      </td>
                      <td className="px-3 py-2 font-label font-semibold text-foreground-600">
                        {entry.timeSignature}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill correct={byKey.note.correct} wrong={byKey.note.wrong} />
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill correct={byKey.attack.correct} wrong={byKey.attack.wrong} />
                      </td>
                      <td className="px-3 py-2">
                        {byKey.duration.notEvaluated ? (
                          <span className="text-[11px] font-label font-semibold text-foreground-500">
                            não avaliada
                          </span>
                        ) : (
                          <StatusPill correct={byKey.duration.correct} wrong={byKey.duration.wrong} />
                        )}
                      </td>
                      {hasRests ? (
                        <td className="px-3 py-2">
                          <StatusPill correct={byKey.rest.correct} wrong={byKey.rest.wrong} />
                        </td>
                      ) : null}
                      {hasTies ? (
                        <td className="px-3 py-2">
                          <StatusPill correct={byKey.tie.correct} wrong={byKey.tie.wrong} />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-foreground-500">
            <i className="ri-check-line mr-1 text-accent-600" />
            = certo ·
            <i className="ri-close-line mx-1 text-secondary-600" />
            = errado. A mesma regra do card vale para cada exercício.
          </p>
        </div>
      ) : null}
    </div>
  );
}